// workerSlice.ts
import { createSlice } from '@reduxjs/toolkit';
import { WORKER_SLICE_NAME } from '../actions/worker.action';
import {
  getWorkerDetail,
  getWorkerImageUrl,
  getWorkers,
  getWorkersAdmins,
  getWorkerSafetyHistories,
  getWorkersBySafetyId
} from '../thunks/worker.thunk';
import {
  IWorker,
  IWorkerDetail,
  ReqFetchWorkers,
  ReqFetchWorkersBySafetyId,
  ReqWorkerSafetyHistories
} from '../model/worker.model';
import { TotalMapProps } from 'modules/common';
import { createWorker, IWorkerSafetyHistory, updateUser } from 'modules/user';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'constants/const';
import { IDialog, IDialogType, IPageAutority } from 'modules/notice/models/notice.model';

export interface WorkerState {
  workers: IWorker[];
  workersTotalMap: TotalMapProps;

  workerDetail: IWorkerDetail;
  safetyHistories: IWorkerSafetyHistory[];
  safetyHistoriesTotalMap: TotalMapProps;

  fetchWorkersRequest: ReqFetchWorkers;
  fetchWorkerSafetyHistoriesRequest: ReqWorkerSafetyHistories;
  fetchWorkersBySafetyIdRequest: ReqFetchWorkersBySafetyId;

  dialog: IDialog;
  pageAuthority: IPageAutority;
}

// 초기 상태 정의
const initialState: WorkerState = {
  workerDetail: {
    email: '',
    birthDate: '',
    bloodType: '',
    designationIdName: '',
    nationName: '',
    iqamaNo: '',
    status: '',
    userId: '',
    workerId: '',
    usernameV2: {
      firstName: '',
      middleName: '',
      lastName: '',
      fullName: '',
      title: ''
    },
    companyId: '',
    companyName: '',
    designationId: '',
    designationName: '',
    mobile: '',
    nationId: '',
    iqamq: '',
    mobilization: '',
    passportAssign: '',
    safetyHistoryList: [],
    mobileNationId: '',
    mobileNationName: '',
    mobileNumberCode: '',
    workerName: '',
    workerType: '',
    role: '',
    file: undefined,
    fileUrl: undefined
  },
  workers: [],
  workersTotalMap: {
    totalElements: 0,
    totalPages: 0
  },

  safetyHistories: [],
  safetyHistoriesTotalMap: {
    totalElements: 0,
    totalPages: 0
  },

  fetchWorkersRequest: {
    company: 'ALL',
    workerType: 'WORKER',
    passport: 'ALL',
    keyword: '',
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE
  },
  fetchWorkerSafetyHistoriesRequest: {
    categoryId: 'ALL',
    trainingId: '',
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE
  },
  fetchWorkersBySafetyIdRequest: {
    company: '',
    workerType: '',
    passport: '',
    keyword: '',
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE
  },
  dialog: {
    type: IDialogType.none,
    message: undefined,
    isNavigate: false,
    callback: () => {}
  },
  pageAuthority: {
    isReadonly: false,
    hasAuthority: false
  }
};

// Slice 생성
const workerSlice = createSlice({
  name: WORKER_SLICE_NAME,
  initialState,
  reducers: {
    changeFetchWorkersRequest: (state, action) => {
      state.fetchWorkersRequest = {
        ...state.fetchWorkersRequest,
        ...action.payload
      };
    },

    changeFetchWorkerSafetyHistoriesRequest: (state, action) => {
      state.fetchWorkerSafetyHistoriesRequest = {
        ...state.fetchWorkerSafetyHistoriesRequest,
        ...action.payload
      };
    },
    resetFetchWorkerSafetyHistoriesRequest: (state) => {
      state.fetchWorkerSafetyHistoriesRequest = initialState.fetchWorkerSafetyHistoriesRequest;
    },

    changeWorkerDetail: (state, action) => {
      state.workerDetail = {
        ...state.workerDetail,
        ...action.payload
      };
    },
    resetWorkerDetail: (state) => {
      state.workerDetail = initialState.workerDetail;
    },

    changeFetchWorkerBySafetyIdRequest: (state, action) => {
      state.fetchWorkersBySafetyIdRequest = {
        ...state.fetchWorkersBySafetyIdRequest,
        ...action.payload
      };
    },

    resetFetchWorkersRequest: (state) => {
      state.fetchWorkersRequest = initialState.fetchWorkersRequest;
    },

    changeDialog: (state, action) => {
      state.dialog = {
        ...state.dialog,
        ...action.payload
      };
    },

    resetDialog: (state) => {
      state.dialog = initialState.dialog;
    },
    changePageAuthority: (state, action) => {
      state.pageAuthority = {
        ...state.pageAuthority,
        ...action.payload
      };
    },

    resetAll: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // 작업자 리스트 조회 성공
      .addCase(getWorkers.fulfilled, (state, action) => {
        state.workers = action.payload.resultList;
        state.workersTotalMap = action.payload.totalMap;
      })

      // 작업자 및 관리자 리스트 조회 성공
      .addCase(getWorkersAdmins.fulfilled, (state, action) => {
        state.workers = action.payload.resultList;
      })

      // 안전 교육 리스트 조회 성공
      .addCase(getWorkersBySafetyId.fulfilled, (state, action) => {
        state.workers = action.payload.resultList;
      })

      // 작업자 상세 정보 조회 성공
      .addCase(getWorkerDetail.fulfilled, (state, action) => {
        state.workerDetail = action.payload.resultObject;
      })

      .addCase(getWorkerImageUrl.fulfilled, (state, action) => {
        state.workerDetail = {
          ...state.workerDetail,
          fileUrl: action.payload
        };
      })

      // 작업자 안전 교육 이력 조회 성공
      .addCase(getWorkerSafetyHistories.fulfilled, (state, action) => {
        state.safetyHistories = action.payload.resultList;
      })

      // 작업자 등록 성공
      .addCase(createWorker.fulfilled, (state, action) => {
        state.dialog.message = action.payload.message;
        state.dialog.isNavigate = true;
        state.dialog.type = IDialogType.alert;
      })
      .addCase(createWorker.rejected, (state, action) => {
        state.dialog.message = action.payload.message;
        state.dialog.type = IDialogType.alert;
      })

      .addCase(updateUser.fulfilled, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.dialog.type = IDialogType.alert;
        state.dialog.message = action.payload.message;
      });
  }
});

// Export
export const worker = workerSlice.name;
export const workerReducer = workerSlice.reducer;
export const workerActions = workerSlice.actions;
export const {
  changeFetchWorkersRequest,
  changeFetchWorkerSafetyHistoriesRequest,
  changeFetchWorkerBySafetyIdRequest,
  changeWorkerDetail,
  resetFetchWorkersRequest,
  changeDialog,
  resetDialog,
  resetWorkerDetail,
  resetAll,
  resetFetchWorkerSafetyHistoriesRequest,
  changePageAuthority
} = workerSlice.actions;
export default workerSlice.reducer;
